/**
 * Shrinks media in the browser before it is uploaded.
 *
 * Photos straight off a phone are routinely 4-8MB, which makes the review page
 * slow for every reader afterwards. Re-drawing through a canvas at a sane size
 * takes most of that away, and it drops the EXIF block with it, so a camera's
 * GPS coordinates never leave the editor's machine.
 *
 * Video is passed through untouched: real transcoding needs ffmpeg, which the
 * browser does not have. `inspectVideo` is what guards that case instead.
 */

export const MAX_IMAGE_EDGE = 1920;
export const IMAGE_QUALITY = 0.82;

// Matches the server; a video past this is refused before the upload starts
export const MAX_VIDEO_BYTES = 120 * 1024 * 1024;

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-m4v'];

export const formatBytes = (bytes) => {
  const n = Number(bytes || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const mediaKind = (file) => {
  const type = (file?.type || '').toLowerCase();
  if (IMAGE_TYPES.includes(type)) return 'image';
  if (VIDEO_TYPES.includes(type)) return 'video';
  // Some browsers hand over an empty type for .mov; fall back to the name
  const name = (file?.name || '').toLowerCase();
  if (/\.(jpe?g|png|gif|webp)$/.test(name)) return 'image';
  if (/\.(mp4|webm|mov|m4v)$/.test(name)) return 'video';
  return null;
};

const readAsImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that image.')); };
  img.src = url;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve) => {
  if (canvas.toBlob) canvas.toBlob((blob) => resolve(blob), type, quality);
  else resolve(null);
});

/**
 * Returns a File no larger than the original. An animated GIF is left alone,
 * since a canvas would flatten it to a single frame.
 */
export async function compressImage(file) {
  if ((file.type || '').toLowerCase() === 'image/gif') return file;

  let img;
  try {
    img = await readAsImage(file);
  } catch {
    return file; // Unreadable here is the server's problem to report
  }

  const { width, height } = img;
  if (!width || !height) return file;

  const longest = Math.max(width, height);
  const scale = longest > MAX_IMAGE_EDGE ? MAX_IMAGE_EDGE / longest : 1;
  const targetW = Math.max(1, Math.round(width * scale));
  const targetH = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;

  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, targetW, targetH);

  // PNG keeps its format so transparency survives; everything else becomes JPEG
  const keepPng = (file.type || '').toLowerCase() === 'image/png';
  const outType = keepPng ? 'image/png' : 'image/jpeg';
  const blob = await canvasToBlob(canvas, outType, keepPng ? undefined : IMAGE_QUALITY);

  // Re-encoding can make a small, already-optimised file bigger; keep the original then
  if (!blob || blob.size >= file.size) return file;

  const base = (file.name || 'photo').replace(/\.[^.]+$/, '');
  const ext = keepPng ? 'png' : 'jpg';
  return new File([blob], `${base}.${ext}`, { type: outType, lastModified: Date.now() });
}

/** Videos are not transcoded, so the size is checked up front instead. */
export function inspectVideo(file) {
  if (file.size > MAX_VIDEO_BYTES) {
    return {
      ok: false,
      error: `That video is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_VIDEO_BYTES)} — `
        + 'trim it or export it at a lower quality first.',
    };
  }
  return { ok: true };
}

/**
 * Prepares one file for upload. Returns { file, kind } or { error }.
 */
export async function prepareMedia(file) {
  const kind = mediaKind(file);
  if (!kind) {
    return { error: 'That file type is not supported. Use a photo (JPG, PNG, GIF, WebP) or a video (MP4, WebM, MOV).' };
  }

  if (kind === 'video') {
    const check = inspectVideo(file);
    if (!check.ok) return { error: check.error };
    return { file, kind, originalSize: file.size };
  }

  const compressed = await compressImage(file);
  return { file: compressed, kind, originalSize: file.size };
}
